import { countries } from '../assets/countryList'

// `EU` is the default jurisdiction for new training sessions (see EditTrainingSessionModal /
// OrganisationJurisdictionSelect), but it isn't an ISO 3166-1 alpha-2 code so it has no entry in
// `countryList`. Handle it explicitly rather than letting it fall through to the "unknown code"
// case below.
const SPECIAL_CASE_LABELS: Record<string, string> = {
	EU: 'European Union',
}

export type JurisdictionParts = {
	/** Full name, or the bare code itself when unknown, or the "–" placeholder when missing. */
	name: string
	/**
	 * Alpha-2 code to render alongside `name`, e.g. for a dimmer "(DE)" suffix - or `null` when
	 * there's no separate code to show (an unknown code, where `name` already *is* the code, or a
	 * missing/empty code).
	 */
	code: string | null
}

/**
 * Resolves an alpha-2 jurisdiction code into its display name and code, for read-only summaries
 * that style the two parts differently (see AdminTrainingSessionDetail's summary box, where the
 * name is white and the code is dimmer, matching the surrounding text).
 *
 * - Known `countryList` codes resolve to their full name plus the code.
 * - `EU` (the training session default - not a real ISO code) resolves to "European Union" plus
 *   the code.
 * - Unknown codes are shown as-is, with no separate code part (the name already is the code).
 * - Missing/empty codes resolve to an en dash placeholder, with no separate code part.
 */
export function formatJurisdictionParts(code?: string | null): JurisdictionParts {
	if (!code) {
		return { name: '–', code: null }
	}

	const specialCaseLabel = SPECIAL_CASE_LABELS[code]
	if (specialCaseLabel) {
		return { name: specialCaseLabel, code }
	}

	const country = countries.find((c) => c.value === code)
	return country ? { name: country.label, code } : { name: code, code: null }
}

/**
 * Formats an alpha-2 jurisdiction code as a single "<full name> (<code>)" string, e.g.
 * `Germany (DE)`. Prefer `formatJurisdictionParts` when the name and code need separate styling
 * (e.g. a dimmer code suffix) - this is for plain-text contexts (aria labels, exports, etc).
 */
export function formatJurisdiction(code?: string | null): string {
	const { name, code: resolvedCode } = formatJurisdictionParts(code)
	return resolvedCode ? `${name} (${resolvedCode})` : name
}
